<template>
  <div class="detail-root">
    <!-- 顶栏：返回 + 账号 -->
    <div class="top-bar">
      <span class="back" @click="goBack">↩</span>
      <div class="account-badge">
        <span class="avatar">{{ userName.charAt(0) }}</span>
        <div class="name-line">
          <span class="name">{{ userName }}</span>
          <span class="nick" v-if="nickName">{{ nickName }}</span>
          <!-- 设备延迟从底部设备条挪到名字下面（底部一窄条字段太多、长文本会挤变形） -->
          <span class="name-lat" :class="deviceLatencyClass" :title="deviceLatencyTitle">
            <span class="nl-label">延迟</span>{{ deviceLatencyText }}
          </span>
        </div>
      </div>
      <div class="top-actions">
        <span class="range-btn" :class="{ on: range === 'today' }" @click="setRange('today')">今</span>
        <span class="range-btn" :class="{ on: range === 'yesterday' }" @click="setRange('yesterday')">昨</span>
        <span class="range-btn" :class="{ on: range === '2d' }" @click="setRange('2d')">2d</span>
        <span class="range-btn" :class="{ on: range === '3d' }" @click="setRange('3d')">3d</span>
        <span class="range-btn" :class="{ on: range === '7d' }" @click="setRange('7d')">7d</span>
      </div>
    </div>

    <!-- 实时状态 -->
    <div class="status-strip">
      <!-- 受控开关：直接写服务端（与「受控房间状态」列表同一接口 SetRoomOpenKillPig），
           作用于账号当前所在房间；未进房 / 不在受控列表 / 维护中 均不可操作 -->
      <div class="ss-item ss-switch">
        <span class="ss-label">单控</span>
        <NSwitch
          size="small"
          :value="!!roomState?.OpenKillPig"
          :loading="roomKillPigLoading"
          :disabled="!canToggleRoomKillPig"
          :title="roomKillPigHint"
          @update:value="onToggleRoomKillPig"
        />
      </div>
      <span class="ss-err" v-if="roomKillPigError">{{ roomKillPigError }}</span>
      <div class="ss-item">
        <span class="ss-label">状态</span>
        <span class="ss-val" :class="online ? 'on' : 'off'">{{ online ? '直播中' : '离线' }}</span>
      </div>
      <div class="ss-item ss-room">
        <span class="ss-label">房间</span>
        <span class="ss-val">{{ resolveRoomName(room) || '未进房' }}</span>
      </div>
      <!-- 在线只显示上线时间，离线只显示最后离线时间（两者不会同时展示） -->
      <div class="ss-item">
        <span class="ss-label">{{ online ? '上线' : '离线' }}</span>
        <span class="ss-val">{{
          online
            ? (onlineTime ? fmtTime(onlineTime) : '—')
            : (lastOfflineTime ? fmtTime(lastOfflineTime) : '—')
        }}</span>
      </div>
    </div>

    <!-- 直播统计：单行紧凑（标签+数值内联，不再占两行大卡片） -->
    <div class="stats-bar" v-if="debugMode">
      <div class="stat-card">
        <span class="sc-label">直播场次</span>
        <span class="sc-val">{{ totalCount }}</span>
      </div>
      <div class="stat-card" :class="netWinCardClass">
        <span class="sc-label">直播收入</span>
        <span class="sc-val" :class="netWinClass">{{ totalNetWinStr }}</span>
      </div>
      <!-- 荷官：取账号当前所在房间的实时状态（与「受控房间状态」同源），重名时高亮 + 角标 -->
      <div class="stat-card stat-dealer" v-if="roomState">
        <span class="sc-label">荷官</span>
        <span class="sc-val dealer-name" :class="{ dup: roomState.IsDuplicateDealer }">
          {{ roomState.DealerName || '未到齐' }}
        </span>
        <!-- 角标：显示当前策略，点一下就地切换（避让 ⇄ 照常），不必进数据面板 -->
        <button
          v-if="roomState.IsDuplicateDealer"
          type="button"
          class="dup-tag"
          :class="{
            'is-avoid': qkNoHostingOnDuplicate && !qkSmartAnchorClean,
            'is-clean': qkSmartAnchorClean,
            busy: dupToggling,
            failed: dupFailed,
          }"
          :disabled="dupToggling"
          :title="dupTitle"
          @click.stop="onToggleDupPolicy"
        >{{ dupToggling ? '切换中…' : dupFailed ? '切换失败' : dupLabel }}</button>
      </div>
      <!-- 房间当前 CD：由锚点面板的推送（currentCd）转发上来，本地每秒倒计时；无 CD 时隐藏 -->
      <div class="stat-card stat-cd" v-if="roomCd > 0" title="当前房间 CD（锚点冷却剩余秒数）">
        <span class="sc-label">CD</span>
        <span class="sc-val cd-val">{{ roomCd }}s</span>
      </div>
    </div>

    <!-- 内容区：撑满剩余空间，内部滚动 -->
    <div class="content-wrap">
      <div class="scroll-area">
        <div v-if="loading" class="loading">正在拉取直播数据...</div>
        <div v-else-if="records.length === 0" class="empty">暂无直播记录</div>

        <!-- 记录网格：手机 1 列；PC 宽屏 2~3 列（卡片保持紧凑单列布局） -->
        <div class="record-grid">
          <div
            class="record"
            v-for="r in records"
            :key="r.order_number || r.id"
            :class="[recordBoxClass(r), { unfinished: !r.is_finish, expanded: killOpen(r), clickable: r.kill_flag > 0 }]"
            @click="onRecordClick(r)"
          >
          <!-- 头部：直播间信息 -->
          <div class="rec-top">
            <div class="rec-top-row1">
              <span class="live-dot" v-if="!r.is_finish"></span>
              <!-- 只显示房间号（EU1/EU5）+ 房间编号（3001-57），游戏名不占地方 -->
              <span class="rec-desk">
                {{ roomDisplayName(r) }}<i v-if="roomNo(r)" class="rec-room-no">{{ roomNo(r) }}</i>
              </span>
              <!-- 直播中不再重复标注（绿点已是直播信号、蓝角标已是待开奖），只标「已结束」 -->
              <span v-if="r.is_finish" class="rec-status done">已结束</span>
              <!-- 托管结论：跳=红标(跳转锚点)、然=绿标(自然死亡)、略=琥珀标(条件不符等跳过)。
                   桌面悬停有 title；查看完整原因=点整条卡片（模式B，手机上点击区域最大），再点收起；
                   同时只展开一条（手风琴），结算推送到达时自动展开。标签带 ▾ 角标，展开后旋转 -->
              <span
                v-if="r.kill_flag === 2"
                class="rec-kill-tag kill"
                :class="{ open: killOpen(r) }"
                :title="`跳转：落在「${r.kill_desc}」的锚点局`"
                @click.stop="toggleKill(r)"
              >跳<i class="k-arrow">▾</i></span>
              <span
                v-else-if="r.kill_flag === 1"
                class="rec-kill-tag natural"
                :class="{ open: killOpen(r) }"
                :title="r.kill_desc"
                @click.stop="toggleKill(r)"
              >然<i class="k-arrow">▾</i></span>
              <span
                v-else-if="r.kill_flag === 3"
                class="rec-kill-tag noskill"
                :class="{ open: killOpen(r) }"
                :title="r.kill_desc"
                @click.stop="toggleKill(r)"
              >略<i class="k-arrow">▾</i></span>
              <span class="rec-result-chip" v-if="debugMode && betLabel(r)" :class="betColorClass(r)">
                {{ betLabel(r) }}<i v-if="r.odds"> · {{ r.odds }}</i>
              </span>
            </div>
            <!-- 点卡片展开的完整原因（手风琴：同时只展开一条；结算推送到达自动展开） -->
            <div v-if="killOpen(r)" class="rec-kill-desc">{{ killFullText(r) }}</div>
          </div>

          <!-- PK 对战区 - 真实模式：传统扑克牌 -->
          <div class="pk-real-showdown" v-if="debugMode && (r.xian_cards || r.zhuang_cards)">
            <!-- 信息行 -->
            <div class="showdown-info">
              <span class="rec-time">{{ formatTime(r.bet_time) }}</span>
              <span class="rec-round">第{{ r.main_ju_number }}回合</span>
              <span class="rec-settle-time" v-if="r.settlement_time">结束于 {{ formatTime(r.settlement_time) }}</span>
            </div>
            <!-- 对决行 -->
            <div class="showdown-row">
              <!-- 我方卡牌 -->
              <div class="playing-card" v-for="(c, ci) in parseCards(r.xian_cards)" :key="'x'+ci" :class="c.suit">
                <span class="pc-rank">{{ realRankDisplay(c.count) }}</span>
                <span class="pc-suit">{{ realSuitSymbol(c.suit) }}</span>
              </div>
              <div v-if="!parseCards(r.xian_cards).length" class="playing-card empty">
                <span class="pc-rank">-</span>
              </div>
              <!-- VS 比分 -->
              <div class="showdown-vs">
                <span class="vs-score-xian" v-if="r.xian_dian != null">{{ r.xian_dian }}</span>
                <span class="vs-word">vs</span>
                <span class="vs-score-zhuang" v-if="r.zhuang_dian != null">{{ r.zhuang_dian }}</span>
              </div>
              <!-- 对方卡牌 -->
              <div class="playing-card" v-for="(c, ci) in parseCards(r.zhuang_cards)" :key="'z'+ci" :class="c.suit">
                <span class="pc-rank">{{ realRankDisplay(c.count) }}</span>
                <span class="pc-suit">{{ realSuitSymbol(c.suit) }}</span>
              </div>
              <div v-if="!parseCards(r.zhuang_cards).length" class="playing-card empty">
                <span class="pc-rank">-</span>
              </div>
            </div>
          </div>

          <!-- PK 对战区 - 礼物模式：图标徽章 -->
          <div class="pk-zone" v-else-if="!debugMode && (r.xian_cards || r.zhuang_cards)">
            <div class="pk-row">
              <span class="pk-tag blue">我方</span>
              <span class="pk-cards">
                <span class="chip" :class="c.suit" v-for="(c, ci) in parseCards(r.xian_cards)" :key="'x'+ci">
                  {{ c.symbol }}×{{ c.count }}
                </span>
                <span v-if="!parseCards(r.xian_cards).length" class="chip empty">-</span>
              </span>
              <span class="pk-pts" v-if="r.xian_dian != null">{{ r.xian_dian }}<i>分</i></span>
            </div>
            <div class="pk-divider"><span>VS</span></div>
            <div class="pk-row">
              <span class="pk-tag red">对方</span>
              <span class="pk-cards">
                <span class="chip" :class="c.suit" v-for="(c, ci) in parseCards(r.zhuang_cards)" :key="'z'+ci">
                  {{ c.symbol }}×{{ c.count }}
                </span>
                <span v-if="!parseCards(r.zhuang_cards).length" class="chip empty">-</span>
              </span>
              <span class="pk-pts" v-if="r.zhuang_dian != null">{{ r.zhuang_dian }}<i>分</i></span>
            </div>
          </div>

          <!-- 无牌面（正在下注未开牌 / 历史无牌记录）：占位区，带上信息行与等待态，避免卡片空旷 -->
          <div class="pk-live" v-else>
            <div class="showdown-info">
              <span class="rec-time">{{ formatTime(r.bet_time) }}</span>
              <span class="rec-round">第{{ r.main_ju_number }}-{{ r.sub_ju_number }}回合</span>
              <span class="rec-settle-time" v-if="r.settlement_time">结束于 {{ formatTime(r.settlement_time) }}</span>
            </div>
            <div class="live-wait">
              <i class="card-back"></i>
              <i class="card-back"></i>
              <span class="live-wait-txt">{{ r.is_finish ? '本局无牌面记录' : '等待开牌…' }}</span>
              <i class="card-back"></i>
              <i class="card-back"></i>
            </div>
          </div>

          <!-- 收益明细 -->
          <div class="rec-amounts">
            <div class="am">
              <span class="am-icon">投</span>
              <span class="am-val gold">{{ Tools.formatMoney(r.total_betting_amount) }}</span>
            </div>
            <!-- 有效投注：仅 PC 宽屏显示（手机保持原来的三项，不挤） -->
            <div class="am am-pc-only">
              <span class="am-icon">效</span>
              <span class="am-val dim">{{ Tools.formatMoney(r.effective_betting_amount) }}</span>
            </div>
            <div class="am">
              <span class="am-icon">赢</span>
              <span class="am-val" :class="winColor(r.real_win_total_amount)">{{ Tools.formatMoney(r.real_win_total_amount) }}</span>
            </div>
            <div class="am">
              <span class="am-icon">额</span>
              <span class="am-val dim">{{ Tools.formatMoney(r.front_balance) }} → {{ Tools.formatMoney(r.after_balance) }}</span>
            </div>
          </div>
        </div>
        </div>
      </div>

      <!-- 授权设备：贴底一窄条（分页之上、不随列表滚动），与「授权管理-设备管理」同源 -->
      <div class="device-strip" v-if="authDevice">
        <!-- 状态只写「在线 / 离线」（配左侧点色）；浏览器已关闭用金点区分（拍照禁用） -->
        <span class="ds-dot" :class="deviceState" :title="deviceState === 'closed' ? '浏览器已关闭' : deviceStateText"></span>
        <span class="ds-state" :class="deviceState">{{ deviceStateText }}</span>
        <!-- 授权状态：对应「授权管理」里的启用开关（status=1 已授权 / 0 已禁用） -->
        <span class="ds-auth" :class="{ 'is-off': !deviceAuthorized }">{{ deviceAuthorized ? '已授权' : '已禁用' }}</span>
        <span class="ds-item">
          <span class="ds-label">授权码</span>
          <span class="ds-val ds-code">{{ authDevice.auth_code || '-' }}</span>
        </span>
        <!-- 插件版本：没上报的（旧版插件）统一显示「老版本」，不显示破折号 -->
        <span
          class="ds-item ds-ver"
          :class="{ 'is-old': !authDevice.version }"
          :title="authDevice.version ? `插件版本 v${authDevice.version}` : '未上报版本号，可能是老版本插件'"
        >
          <span class="ds-val">{{ deviceVersionText }}</span>
        </span>
        <!-- 默认只露中文归属地；IP 属敏感信息，点这一节才弹出（唯一的可压缩段） -->
        <span class="ds-item ds-region" title="点击查看 IP 地址" @click="ipPop = !ipPop">
          <span class="ds-val">{{ deviceRegionText }}</span>
        </span>
      </div>

      <!-- 点归属地弹出的小卡片：显示 IP（Teleport，避免被设备条的 overflow 裁掉） -->
      <Teleport to="body">
        <div v-if="ipPop" class="ip-mask" @click="ipPop = false"></div>
        <div v-if="ipPop" class="ip-card">
          <div class="ip-row">
            <span class="ip-k">IP</span>
            <span class="ip-v ip-addr">{{ authDevice?.ip || '-' }}</span>
          </div>
          <div class="ip-row">
            <span class="ip-k">归属地</span>
            <span class="ip-v">{{ deviceRegionText }}</span>
          </div>
          <div class="ip-row">
            <span class="ip-k">插件版本</span>
            <span class="ip-v ip-ver">{{ deviceVersionText }}</span>
          </div>
        </div>
      </Teleport>

      <!-- 底部分页：始终在底部 -->
      <div class="pager-bar">
        <span class="pg-btn" :class="{ off: currentPage <= 1 }" @click="goPage(currentPage - 1)">‹</span>
        <span class="pg-num" v-if="totalPages > 0">{{ currentPage }} / {{ totalPages }} 页</span>
        <span class="pg-num" v-else>暂无数据</span>
        <span class="pg-btn" :class="{ off: currentPage >= totalPages }" @click="goPage(currentPage + 1)">›</span>
      </div>
    </div>

    <!-- 设备截图预览：盖在最上层（Teleport 到 body，避免被卡片的 overflow/border-radius 裁掉） -->
    <Teleport to="body">
      <div v-if="shotUrl" class="shot-mask" @click.self="shotUrl = ''">
        <div class="shot-panel">
          <div class="shot-head">
            <span class="shot-title">设备截图</span>
            <span class="shot-meta">
              {{ authDevice?.auth_code || '' }}<template v-if="shotAt"> · {{ fmtTime(shotAt) }}</template>
            </span>
            <a class="shot-link" :href="shotUrl" target="_blank" rel="noreferrer">新窗口打开</a>
            <span class="shot-close" title="关闭" @click="shotUrl = ''">✕</span>
          </div>
          <div class="shot-body">
            <img :src="shotUrl" class="shot-img" alt="设备截图" />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 悬浮拍照（左下，与右下刷新对称）：让插件截一张当前页面 -->
    <span
      v-if="authDevice"
      class="float-shot"
      :class="{ off: !deviceCanShot, busy: shotSending }"
      :title="deviceShotHint"
      @click="handleCaptureDeviceShot"
    >{{ shotSending ? '···' : '拍照' }}</span>

    <!-- 悬浮刷新 -->
    <span class="float-refresh" @click="loadData()">↻</span>

    <!-- 全局控制（总控）：托管开关 + 参与门槛 + 锚点状态，三者合并进同一个悬浮窗 -->
    <QuickKillPanel
      :enabled="qkEnabled"
      :partial="qkPartial"
      :enabled-count="qkEnabledCount"
      :amount="qkAmount"
      :room-count="qkRoomCount"
      :room="room"
      :room-name="resolveRoomName(room)"
      :on-save="handleQkSave"
      :on-refresh="qkLoad"
      @cd-change="roomCd = $event"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, inject, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { NSwitch } from 'naive-ui';
import Tools from '@/ts/Tools';
import { GAME_TYPE_MAP } from '@/ts/roomName';
import { resolveRoomName, setRoomNameMap } from '@/ts/roomNameStore';
import RoomVO from '@/ts/VO/RoomVO';
import RoomStateInfo from '@/ts/VO/RoomStateInfo';
import SignalRClient from '@/ts/SignalRClient';
import Config from '@/ts/Config';
import QuickKillPanel from '@/components/QuickKillPanel.vue';
import { useQuickKill } from '@/ts/useQuickKill';

const route = useRoute();
const router = useRouter();
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));
const userName = ref(route.params.userName as string || '');
const nickName = ref('');

// ===== 全局控制面板（托管开关 + 参与门槛）=====
// 详情页是独立路由，不经过 HomeView，面板的数据需自行从服务端拉取
const {
  enabled: qkEnabled,
  partial: qkPartial,
  enabledCount: qkEnabledCount,
  amount: qkAmount,
  roomCount: qkRoomCount,
  noHostingOnDuplicate: qkNoHostingOnDuplicate,
  smartAnchorClean: qkSmartAnchorClean,
  load: qkLoad,
  save: qkSave,
  toggleNoHostingOnDuplicate,
} = useQuickKill();

// 重名策略就地切换（荷官角标按钮）：切换中防连点，失败短暂显示「切换失败」
const dupToggling = ref(false);
const dupFailed = ref(false);
let dupFailedTimer: number | null = null;

async function onToggleDupPolicy(): Promise<void> {
  if (dupToggling.value) return;
  dupToggling.value = true;
  try {
    const ok = await toggleNoHostingOnDuplicate();
    if (!ok) throw new Error('SetKillPigConfig 返回 false');
  } catch (e) {
    console.warn('[dup] 切换重名策略失败:', e);
    dupFailed.value = true;
    if (dupFailedTimer != null) clearTimeout(dupFailedTimer);
    dupFailedTimer = window.setTimeout(() => { dupFailed.value = false; }, 2500);
  } finally {
    dupToggling.value = false;
  }
}

// 返回时回到主播列表页（首页的“主播列表”标签）
function goBack() {
  router.push({ name: 'home', query: { tab: 'recharge' } });
}

// ===== 授权设备（授权码 / IP·地区）=====
// 数据与「授权管理-设备管理」同源：REST auth/list-devices 拉静态字段，
// SignalR onDeviceUpdate 推实时字段（在线、IP、延迟）。
// 展示为页面底部、分页之上的固定窄条（不随列表滚动）；延迟已移到顶栏名字下方，
// 不再占底部窄条的位置（字段太多、长文本会把它挤变形）。
type AuthDeviceBrief = {
  device_id?: string;
  auth_code?: string;
  status?: number;
  ip?: string;
  region?: string;
  online?: boolean | number;
  /** 插件版本号（扩展 WebSocket 的 ver= 参数上报；旧版扩展为空） */
  version?: string;
  /** 是否还有浏览器窗口（false = 插件后台常驻但窗口已全部关闭） */
  browser_open?: boolean;
  window_count?: number;
  latency?: number;
  latency_line?: number;
  last_seen_time?: number;
  last_login_time?: number;
};

const authDevice = ref<AuthDeviceBrief | null>(null);
/** IP 小卡片是否展开（设备条默认只显示中文归属地） */
const ipPop = ref(false);

/** 连接是否还在（扩展后台常驻也算在线） */
const deviceOnline = computed(() => {
  const d = authDevice.value;
  if (!d) return false;
  return d.online === true || Number(d.online) === 1;
});

/**
 * 设备展示态（与「授权管理-设备管理」同一口径）：
 * on=在线（有浏览器窗口） / closed=浏览器已关闭（扩展后台常驻，连接还在） / off=离线
 */
const deviceState = computed<'on' | 'closed' | 'off'>(() => {
  const d = authDevice.value;
  if (!d || !deviceOnline.value) return 'off';
  // 旧接口没有 browser_open 字段时按原来的在线处理
  return d.browser_open === false ? 'closed' : 'on';
});

/**
 * 条上只写最短的两个字：在线 / 离线。
 * 语境里就是设备状态（左边还有点色），不必再写「设备」二字。
 * 「浏览器已关闭」只体现在金点上（拍照仍禁用），不占文字宽度。
 */
const deviceStateText = computed(() => (deviceState.value === 'on' ? '在线' : '离线'));

/** 是否已授权：对应「授权管理」设备卡片上的启用开关（status 1=启用 / 0=禁用） */
const deviceAuthorized = computed(() => Number(authDevice.value?.status) === 1);

/** 设备条上默认展示的归属地（中文地区；IP 点开才看） */
const deviceRegionText = computed(() => authDevice.value?.region || '未知地区');

/** 插件版本：旧版插件不上报版本号，统一显示「老版本」而不是空/破折号 */
const deviceVersionText = computed(() => (authDevice.value?.version ? `v${authDevice.value.version}` : '老版本'));

/** 只有「在线且有窗口」才能拍照/遥控，与授权管理页的按钮禁用条件一致 */
const deviceCanShot = computed(() => deviceState.value === 'on');

const deviceShotHint = computed(() => {
  if (deviceState.value === 'on') return '让插件截一张当前页面';
  if (deviceState.value === 'closed') return '浏览器已关闭，无法拍照（需先打开浏览器窗口）';
  return '设备离线，无法拍照';
});

/** -1 = 未知（离线 / 旧版插件未上报 / 刚连上还没测到） */
function toLatencyMs(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : -1;
}

/** 主地址延迟（WebSocket 往返） */
const deviceLatency = computed(() => toLatencyMs(authDevice.value?.latency));
/** 线路地址延迟（到线路域名的 HTTP 往返，与主地址相同时不上报） */
const deviceLatencyLine = computed(() => toLatencyMs(authDevice.value?.latency_line));

const deviceLatencyText = computed(() => {
  const parts: string[] = [];
  if (deviceLatency.value >= 0) parts.push(`${deviceLatency.value}ms`);
  if (deviceLatencyLine.value >= 0) parts.push(`线 ${deviceLatencyLine.value}ms`);
  return parts.length ? parts.join(' · ') : '-';
});

const deviceLatencyClass = computed(() => {
  const v = deviceLatency.value >= 0 ? deviceLatency.value : deviceLatencyLine.value;
  if (v < 0) return '';
  if (v <= 120) return 'lat-good';
  if (v <= 300) return 'lat-warn';
  return 'lat-bad';
});

const deviceLatencyTitle = computed(() => {
  const tips: string[] = [];
  if (deviceLatency.value >= 0) tips.push(`主 ${deviceLatency.value}ms（WebSocket 往返）`);
  if (deviceLatencyLine.value >= 0) tips.push(`线路 ${deviceLatencyLine.value}ms（到线路域名的 HTTP 首次响应）`);
  return tips.length ? tips.join('；') : '暂无延迟数据（设备离线或插件未上报）';
});

/** 多台设备时挑一台展示：有浏览器窗口优先 → 在线 → 已启用 → 最近心跳/最后登录 */
function pickAuthDevice(list: AuthDeviceBrief[]): AuthDeviceBrief | null {
  if (!list.length) return null;
  const score = (d: AuthDeviceBrief) => {
    const openFlag = d.browser_open === false ? 0 : 1;
    const onlineFlag = d.online === true || Number(d.online) === 1 ? 1 : 0;
    const enabledFlag = Number(d.status) === 1 ? 1 : 0;
    const seen = Number(d.last_seen_time) || Number(d.last_login_time) || 0;
    return openFlag * 1e20 + onlineFlag * 1e18 + enabledFlag * 1e16 + seen;
  };
  return [...list].sort((a, b) => score(b) - score(a))[0];
}

async function loadAuthDevice() {
  try {
    const resp = await Tools.FetchGetAsync<{ list: AuthDeviceBrief[] }>('auth/list-devices', {
      page: 1,
      limit: 50,
      status: -1,
      sort: 'last_login',
    });
    if (!resp.CheckIsOk()) return;
    authDevice.value = pickAuthDevice(resp.Data?.list ?? []);
  } catch {
    // 设备信息拿不到不影响详情页主功能，静默忽略
  }
}

// ---- 拍照：下发 CaptureScreenshot，插件截屏后由 onDeviceScreenshot 推回图片地址 ----
const shotSending = ref(false);
/** 当前预览的截图地址（空 = 不显示预览） */
const shotUrl = ref('');
const shotAt = ref(0);
let shotTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

function clearShotTimeout() {
  if (shotTimeoutTimer) {
    window.clearTimeout(shotTimeoutTimer);
    shotTimeoutTimer = null;
  }
}

function armShotTimeout() {
  clearShotTimeout();
  shotTimeoutTimer = window.setTimeout(() => {
    shotTimeoutTimer = null;
    Tools.AlertError('拍照超时：20 秒内未收到截图，请确认浏览器窗口可见且未最小化');
  }, 20000);
}

async function handleCaptureDeviceShot() {
  const deviceId = authDevice.value?.device_id;
  if (!deviceId || !deviceCanShot.value || shotSending.value) return;
  shotSending.value = true;
  try {
    const resp = await SignalRClient.Instance.InvokeAsync<{ flag: boolean; errorMessage: string }>(
      'CaptureScreenshot',
      deviceId
    );
    if (resp?.flag) {
      armShotTimeout();
      Tools.AlertSuccess('拍照指令已发送，请稍候…');
    } else {
      Tools.AlertError(resp?.errorMessage || '拍照指令发送失败，设备可能不在线');
    }
  } catch (e: any) {
    Tools.AlertError(e?.message || '拍照指令发送失败');
  } finally {
    shotSending.value = false;
  }
}

/** 截图回推：先清超时，再弹预览 */
function onDeviceScreenshot(st: any) {
  if (!st?.url) return;
  clearShotTimeout();
  shotUrl.value = st.url.startsWith('http') ? st.url : `${Config.ORIGIN}${st.url}`;
  shotAt.value = st.ts || 0;
}

/** 截图失败（窗口最小化 / 无活动标签 / 上传失败等） */
function onDeviceScreenshotFailed(st: any) {
  clearShotTimeout();
  Tools.AlertError(`截图失败：${st?.error || '未知原因'}`);
}

/** 设备实时状态推送：只更新当前展示的那台 */
function onDeviceUpdate(st: any) {
  const cur = authDevice.value;
  if (!cur || !st?.deviceId || st.deviceId !== cur.device_id) return;
  if (st.online !== undefined) cur.online = st.online;
  if (st.windowCount !== undefined) cur.window_count = st.windowCount;
  if (st.browserOpen !== undefined) cur.browser_open = st.browserOpen;
  if (st.ip) cur.ip = st.ip;
  if (st.region) cur.region = st.region;
  if (st.version) cur.version = st.version;
  if (st.latency != null) cur.latency = st.latency;
  if (st.latencyLine != null) cur.latency_line = st.latencyLine;
  if (st.lastSeen != null) cur.last_seen_time = st.lastSeen;
}

// 实时状态（来自列表接口 / GetAccountBets / 服务端推送）
// 进入详情时先用列表已知的实时状态做种子，避免接口滞后导致误判“离线”
const online = ref(route.query.online === '1');
const onlineTime = ref((route.query.ot as string) || '');
const lastOfflineTime = ref((route.query.lo as string) || '');
const room = ref((route.query.room as string) || '');

// ===== 受控开关（账号当前所在房间的托管，口径与「受控房间状态」列表一致）=====
// 房间号来自服务端实时状态（GameType-GameCode，如 3001-120），开关状态从 GetRoomStates 里取，
// 切换直接调 SetRoomOpenKillPig，不做本页批量保存（避免和首页 selectRooms 全量覆盖打架）。
const roomState = ref<RoomStateInfo | null>(null);

/** 重名角标文案：智能清理（不做避让）优先，其次避让 / 照常 */
const dupLabel = computed(() => {
  if (qkSmartAnchorClean.value) return '重名·清理';
  return qkNoHostingOnDuplicate.value ? '重名·避让' : '重名·照常';
});

const dupTitle = computed(() => {
  if (qkSmartAnchorClean.value)
    return '智能清理：不做重名避让，换主播时清掉其他房间的同名残留锚点（点击切回重名避让）';
  return (qkNoHostingOnDuplicate.value
    ? '重名避让：同一系列内出现同名主播，该房间不参与托管'
    : '重名照常：同一系列内出现同名主播，仍允许该房间参与托管') + '（点击切换）';
});

/** 当前房间 CD 剩余秒数（来自 AnchorFloatPanel 的推送，0 = 无 CD） */
const roomCd = ref(0);
const roomKillPigLoading = ref(false);
const roomKillPigError = ref('');
let roomKillPigErrTimer: number | null = null;
/** 房间状态轮询（荷官名/重名/托管），页面停留期间保持最新 */
let roomStateTimer: number | null = null;
/** 设备信息补拉计数（授权状态没有实时推送，每 6 个周期=60s 重拉一次） */
let deviceTick = 0;

const canToggleRoomKillPig = computed(() =>
  !!roomState.value && !roomState.value.IsMaintaining && !!room.value && !roomKillPigLoading.value
);

const roomKillPigHint = computed(() => {
  if (!room.value) return '账号未在房间中，无法设置受控';
  if (!roomState.value) return `房间 ${room.value} 不在受控房间列表中`;
  if (roomState.value.IsMaintaining) return '房间维护中，不可操作托管';
  return `${room.value} 托管：${roomState.value.OpenKillPig ? '已开启' : '已关闭'}`;
});

function showRoomKillPigError(msg: string) {
  roomKillPigError.value = msg;
  if (roomKillPigErrTimer != null) window.clearTimeout(roomKillPigErrTimer);
  roomKillPigErrTimer = window.setTimeout(() => { roomKillPigError.value = ''; }, 4000);
}

/** 拉取当前房间的受控状态（房间不存在于受控列表时为 null → 开关禁用） */
async function syncRoomState() {
  const roomNumber = room.value;
  if (!roomNumber) {
    roomState.value = null;
    return;
  }
  try {
    const list = await SignalRClient.Instance.InvokeAsync<RoomStateInfo[]>('GetRoomStates');
    // 兼容 RoomNumber 字段缺失（前端类的 getter 在反序列化对象上不存在）
    roomState.value = (list ?? []).find(
      (r) => `${r.GameType}-${r.GameCode}` === roomNumber || r.RoomNumber === roomNumber
    ) ?? null;
  } catch {
    /* 静默：开关保持上一次状态，下次房间变化/列表更新时再同步 */
  }
}

async function onToggleRoomKillPig(open: boolean) {
  const st = roomState.value;
  if (!st || !canToggleRoomKillPig.value) return;
  roomKillPigLoading.value = true;
  try {
    const ok = await SignalRClient.Instance.InvokeAsync<boolean>(
      'SetRoomOpenKillPig', st.GameType, st.GameCode, open
    );
    if (ok !== true) {
      // 服务端没落地（房间已不存在/异常）：不做乐观更新，回读真实状态
      showRoomKillPigError('切换失败：房间不存在或服务端异常');
      await syncRoomState();
      return;
    }
    st.OpenKillPig = open; // 乐观更新，下次同步以服务端为准
    // 联动「总控」：房间开关变了，全开/部分/全关三态要立刻跟着变
    qkLoad().catch(() => {});
  } catch {
    showRoomKillPigError('切换失败：连接异常');
    await syncRoomState();
  } finally {
    roomKillPigLoading.value = false;
  }
}

/**
 * 总控保存：保存成功后立即回读当前房间的受控状态，
 * 让状态条上的「受控」开关跟上总控（原来要刷新页面才变）。
 */
async function handleQkSave(payload: { enabled: boolean; amount: number }) {
  const ok = await qkSave(payload);
  if (ok) {
    await syncRoomState();
  }
  return ok;
}

// 账号换房（含离线清空）时重新取受控状态；同时把 CD 清零，
// 否则会一直显示上一房间的倒计时（新房间无 CD 时更明显）
watch(room, () => {
  roomCd.value = 0;
  syncRoomState();
});

// 房间列表被清空（关闭拉取数据）时锚点面板会卸载，不会再推 CD，这里兜底清零
watch(qkRoomCount, (v) => {
  if (!v) roomCd.value = 0;
});

const range = ref('today');
const loading = ref(false);
const records = ref<any[]>([]);
const currentPage = ref(1);
const totalPages = ref(0);
const totalCount = ref(0);
const totalNetWin = ref(0);
const pageSize = 20;

// 推送已处理过的订单号 / 该订单已计入的收入金额（用于去重，重新拉页时清空）
const pushedOrders = new Set<string>();
const pushedWin = new Map<string, number>();

// ===== 伪装/真实卡牌切换（由父组件统一 debugMode 控制） =====
const REAL_SUIT: Record<string, string> = { spade: '♠', heart: '♥', club: '♣', diamond: '♦' };
const REAL_RANK: Record<number, string> = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };
function realSuitSymbol(suit: string) { return REAL_SUIT[suit] || ''; }
function realRankDisplay(n: number) { return REAL_RANK[n] || String(n); }

const totalNetWinStr = computed(() => {
  const v = totalNetWin.value;
  return Tools.formatMoney(v, { showSign: true });
});
const netWinClass = computed(() => totalNetWin.value > 0 ? 'positive' : totalNetWin.value < 0 ? 'negative' : '');
const netWinCardClass = computed(() => totalNetWin.value > 0 ? 'card-win' : totalNetWin.value < 0 ? 'card-lose' : '');
function setRange(r: string) {
  range.value = r;
  currentPage.value = 1;
  loadData();
}

function goPage(p: number) {
  if (p < 1 || p > totalPages.value) return;
  currentPage.value = p;
  loadData();
}

async function loadData() {
  loading.value = true;
  try {
    const now = new Date();
    let start: string, end: string = toDateStr(now);
    if (range.value === 'today') start = end;
    else if (range.value === 'yesterday') { start = end = toDateStr(new Date(now.getTime() - 86400000)); }
    else if (range.value === '2d') start = toDateStr(new Date(now.getTime() - 86400000));
    else if (range.value === '3d') start = toDateStr(new Date(now.getTime() - 2 * 86400000));
    else if (range.value === '7d') start = toDateStr(new Date(now.getTime() - 6 * 86400000));
    else start = toDateStr(new Date(now.getTime() - 29 * 86400000));

    const resp = await Tools.FetchGetAsync<any>('game/account-bets', {
      userName: userName.value,
      startDate: start,
      endDate: end,
      page: currentPage.value,
      pageSize: pageSize
    });
    if (resp.CheckIsOk() && resp.Data) {
      records.value = resp.Data.data || [];
      // 整页重拉：服务端数据才是准的，清空推送去重账本
      pushedOrders.clear();
      pushedWin.clear();
      totalPages.value = resp.Data.pagecount || 0;
      totalCount.value = resp.Data.allcount || 0;
      // 后端聚合的统计，不再前端拉全量数据计算
      if (resp.Data.stats) {
        totalNetWin.value = resp.Data.stats.total_win || 0;
      }
      if (records.value.length > 0 && records.value[0].nickName) {
        nickName.value = records.value[0].nickName;
      }
      // 实时状态字段（服务端实时计算）
      // online 以“已确认为在线”为准：列表种子或服务端推送若已判在线，接口的滞后 false 不将其降级
      online.value = online.value || !!resp.Data.online;
      onlineTime.value = resp.Data.onlineTime || onlineTime.value;
      lastOfflineTime.value = resp.Data.lastOfflineTime || lastOfflineTime.value;
      room.value = resp.Data.room || room.value;
    }
  } finally {
    loading.value = false;
  }
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ===== 直播间互动玩法映射（见 @/ts/roomName）=====
// 头部只显示房间号（EU1/EU5…）：游戏名（人气对决等）由页面级信息表达，记录头部不再重复占地方
function roomDisplayName(r: any): string {
  return r.game_zhuo_name || r.game_code || '';
}

// 房间编号（如 3001-57）：game_type-game_code，跟在房间号后面用弱化小字展示
function roomNo(r: any): string {
  if (r.game_type && r.game_code) return `${r.game_type}-${r.game_code}`;
  return '';
}

// ===== 直播互动结果判断（仅 debug/真实模式显示） =====

// 托管结论小行展开状态（按记录 key 记忆；手机端没悬停，点卡片展开看完整原因）。
// 必须用 ref：普通 Set 的改动不触发响应式更新，点了卡片界面不会变。
const killOpenMap = ref<Record<string, boolean>>({});
function recKey(r: any): string { return String(r.order_number || r.id || ''); }
function killOpen(r: any): boolean { return !!killOpenMap.value[recKey(r)]; }
function toggleKill(r: any): void {
  const k = recKey(r);
  const isOpen = !!killOpenMap.value[k];
  // 手风琴：同时只展开一个 —— 点开新卡，上一个自动收起；再点已展开的则收起
  killOpenMap.value = isOpen ? {} : { [k]: true };
}
// 模式B：点整条卡片展开/收起托管原因（手机上点击区域最大，不用精准点小标签）。
// 没有托管结论的卡片点了不反应；标签自身点击带 .stop，不会和卡片点击打架。
function onRecordClick(r: any): void {
  if (!(Number(r.kill_flag) > 0)) return;
  toggleKill(r);
}
function killFullText(r: any): string {
  if (r.kill_flag === 2) return `托管跳转：落在「${r.kill_desc || ''}」的锚点局`;
  return r.kill_desc || '';
}

function betLabel(r: any): string {
  // 下注那一刻就知道下的是什么（onBet 推送即带 bet_direction），不必等结算
  return r.bet_direction || '';
}

function betColorClass(r: any): string {
  // 未结算：蓝色「已下注·待开奖」态；结算后按盈亏上色
  if (!r.is_finish) return 'result-betting';
  const win = Number(r.real_win_total_amount) || 0;
  if (win > 0) return 'result-win';
  if (win < 0) return 'result-lose';
  return 'result-tie';
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function winColor(val: number): string {
  if (val > 0) return 'positive';
  if (val < 0) return 'negative';
  return 'zero';
}

// 卡片底色按「玩家输赢」上色：正在下注=琥珀金 / 赢=绿 / 输=红 / 和(退本金)=中性金
function recordBoxClass(r: any): string {
  if (!r.is_finish) return 'res-betting';
  const win = Number(r.real_win_total_amount) || 0;
  if (win > 0) return 'res-win';
  if (win < 0) return 'res-lose';
  return 'res-tie';
}

// ===== 卡牌解析 =====
const SUIT_MAP: Record<string, { symbol: string; cls: string }> = {
  S: { symbol: '🖤', cls: 'spade' },    // 黑桃 — 黑色心/桃
  H: { symbol: '❤️', cls: 'heart' },    // 红桃 — 红色心
  C: { symbol: '🌸', cls: 'club' },     // 梅花 — 花
  D: { symbol: '🟦', cls: 'diamond' },  // 方片 — 方块
};
const RANK_VALUE: Record<string, number> = { '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, '11': 11, '12': 12, '13': 13 };

function parseCards(raw: string | null): { count: number; symbol: string; suit: string }[] {
  if (!raw || raw === '-' || raw === '[]') return [];
  try {
    const arr: string[] = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((c: string) => typeof c === 'string' && c.includes('.'))
      .map((c: string) => {
        const [suit, rank] = c.split('.');
        const info = SUIT_MAP[suit] || { symbol: suit, cls: '' };
        return { count: RANK_VALUE[rank] || 0, symbol: info.symbol, suit: info.cls };
      });
  } catch {
    return [];
  }
}

// 下注/结算推送：服务端已带完整记录，前端直接插入/更新列表，不重新拉取整页
function applyPushedRecord(rec: any) {
  // 只处理当前查看的账号；列表按 bet_time 倒序，新记录只可能落在第 1 页
  if (!rec || !rec.order_number) return;
  if (rec.userName !== userName.value) return;
  if (currentPage.value !== 1) return;
  // “昨天”区间不含今天，推送的新记录不属于该区间，直接丢弃
  if (range.value === 'yesterday') return;

  const no = rec.order_number;
  const idx = records.value.findIndex((r: any) => r && r.order_number === no);
  if (idx >= 0) {
    // 已存在（例如刚打开页面已经刷到这条）→ 原地更新，不新增条目，避免重复
    records.value[idx] = rec;
  } else if (!pushedOrders.has(no)) {
    // 新记录插到最前；超过每页固定条数则移除末尾多余的（推送 100 条也只显示一页）
    records.value = [rec, ...records.value].slice(0, pageSize);
    totalCount.value += 1;                                   // 直播场次
    totalPages.value = Math.max(totalPages.value, Math.ceil(totalCount.value / pageSize));
  }
  pushedOrders.add(no);

  // 结算推送到达：自动展开这条的托管原因（手风琴，只展开这一个；下注推送不展开）
  if (rec.is_finish === 1 && Number(rec.kill_flag) > 0) {
    killOpenMap.value = { [recKey(rec)]: true };
  }

  // 直播收入：仅已结算才计入；同一单只累加差额，避免下注→结算两次推送重复计
  if (rec.is_finish === 1) {
    const win = Number(rec.real_win_total_amount) || 0;
    totalNetWin.value += win - (pushedWin.get(no) || 0);
    pushedWin.set(no, win);
  }
  if (rec.nickName) nickName.value = rec.nickName;
}
const onAccountDataPush = (data: any) => applyPushedRecord(data?.record);

// 房间列表变化 → 重新同步面板（房间数决定托管开关是否可点），并刷新当前房间的受控状态
const onRoomListUpdate = () => {
  qkLoad().catch(() => {});
  syncRoomState();
};

onMounted(() => {
  loadData();

  // 全局控制面板：拉取托管开关/门槛/房间数（深链进入也能正确显示）
  qkLoad().catch(() => {});
  // 受控开关：拉取账号当前房间的托管状态（深链进入时 room 来自 query）
  syncRoomState();
  // 授权设备（授权码 / IP·地区 / 延迟）
  loadAuthDevice();

  // 荷官名 / 重名 / 托管会随直播进程变化，停留期间每 10s 轻量同步一次（后台标签页不拉）
  roomStateTimer = window.setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    // 设备未完全就绪（离线 / 浏览器已关闭）时顺手重拉设备列表；
    // 在线且有窗口后在线/延迟靠 onDeviceUpdate 实时更新，但「授权状态」没有推送，每 60s 补拉一次
    deviceTick += 1;
    if (deviceState.value !== 'on' || deviceTick % 6 === 0) loadAuthDevice();
    if (online.value && room.value) syncRoomState();
  }, 10000);
  // 房间列表变化（服务端拉取数据开关切换）时同步面板的房间数
  SignalRClient.Instance.AddEventListener('onRoomListUpdate', onRoomListUpdate);
  // 授权设备实时状态（在线/IP/延迟）
  SignalRClient.Instance.AddEventListener('onDeviceUpdate', onDeviceUpdate);
  // 设备拍照：成功推图 / 失败推错误原因
  SignalRClient.Instance.AddEventListener('onDeviceScreenshot', onDeviceScreenshot);
  SignalRClient.Instance.AddEventListener('onDeviceScreenshotFailed', onDeviceScreenshotFailed);

  // 兜底：深链/刷新进入时首页未加载房间列表，主动拉一次填充 RoomNumber->RoomName 映射
  SignalRClient.Instance.InvokeAsync<{ [key: string]: RoomVO }>("GetRoomList")
    .then((resp) => {
      const list: RoomVO[] = [];
      for (const key in resp) {
        const room = resp[key];
        Object.setPrototypeOf(room, RoomVO.prototype);
        list.push(room);
      }
      setRoomNameMap(list);
    })
    .catch(() => {});
  // 服务端推送的账号状态实时更新（在线/房间/时间）
  SignalRClient.Instance.AddEventListener("onUserStatusUpdate", (data: any) => {
    if (data?.userName !== userName.value) return;
    online.value = !!data.online;
    onlineTime.value = data.onlineTime || '';
    lastOfflineTime.value = data.lastOfflineTime || '';
    room.value = data.room || '';
  });
  // 下注/结算推送：服务端带完整记录，前端直接插入/更新（去重 + 限制每页条数）
  SignalRClient.Instance.AddEventListener("onBet", onAccountDataPush);
  SignalRClient.Instance.AddEventListener("onSettle", onAccountDataPush);
});

onUnmounted(() => {
  SignalRClient.Instance.RemoveEventListener("onUserStatusUpdate");
  SignalRClient.Instance.RemoveEventListener("onBet");
  SignalRClient.Instance.RemoveEventListener("onSettle");
  SignalRClient.Instance.RemoveEventListener("onRoomListUpdate");
  SignalRClient.Instance.RemoveEventListener("onDeviceUpdate");
  SignalRClient.Instance.RemoveEventListener("onDeviceScreenshot");
  SignalRClient.Instance.RemoveEventListener("onDeviceScreenshotFailed");
  clearShotTimeout();
  if (roomKillPigErrTimer != null) {
    window.clearTimeout(roomKillPigErrTimer);
    roomKillPigErrTimer = null;
  }
  if (roomStateTimer != null) {
    window.clearInterval(roomStateTimer);
    roomStateTimer = null;
  }
  if (dupFailedTimer != null) {
    window.clearTimeout(dupFailedTimer);
    dupFailedTimer = null;
  }
});

function fmtTime(v: any): string {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
</script>

<style scoped lang="scss">
.detail-root {
  position: relative;
  // 顶部一道金线（背景层实现，随卡片固定不随内容滚动）
  background-image:
    linear-gradient(90deg, transparent, rgba(246,226,178,.55) 22%, rgba(255,246,224,.85) 50%, rgba(246,226,178,.55) 78%, transparent),
    var(--kp-card-bg);
  background-size: 100% 1px, auto;
  background-repeat: no-repeat, no-repeat;
  background-position: top left, center;
  border: 1px solid var(--kp-gold-line-strong);
  border-radius: var(--kp-radius);
  box-shadow: var(--kp-card-shadow);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// ===== 顶栏 =====
.top-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--kp-gold-line);
  flex-shrink: 0;
}

.back {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  font-size: 15px;
  color: var(--kp-gold);
  font-weight: 700;
  background: rgba(216, 178, 95, 0.08);
  border: 1px solid rgba(216, 178, 95, 0.20);
  transition: all .15s;
  user-select: none;

  &:hover {
    background: rgba(216, 178, 95, 0.16);
    border-color: rgba(240, 208, 138, 0.45);
    box-shadow: 0 0 18px -8px rgba(240, 208, 138, 0.7);
    color: var(--kp-gold-hi);
  }
}

.account-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(180deg, #fbeecb 0%, #e8c98a 42%, #cfa559 100%);
  color: #241a09;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  flex-shrink: 0;
  box-shadow:
    0 0 0 1px rgba(255, 240, 205, 0.45),
    0 0 22px -8px rgba(240, 208, 138, 0.65);
}

.name-line {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.name {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: .6px;
  color: var(--kp-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nick {
  font-size: 11px;
  color: var(--kp-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// 延迟：名字下面一行小字，颜色按区间区分（绿/金/红），与设备条时代的口径一致
.name-lat {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  max-width: 100%;
  overflow: hidden;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--kp-text-2);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  .nl-label {
    color: var(--kp-faint);
    font-weight: 500;
  }

  &.lat-good { color: var(--kp-jade); }
  &.lat-warn { color: var(--kp-gold-hi); }
  &.lat-bad { color: var(--kp-bad-soft); }
}

.top-actions {
  display: flex;
  gap: 2px;
  background: rgba(255, 240, 210, 0.035);
  border: 1px solid rgba(216, 178, 95, 0.12);
  border-radius: 9px;
  padding: 3px;
  flex-shrink: 0;
}

.range-btn {
  font-size: 11px;
  color: var(--kp-muted);
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: all .15s;

  &:hover { color: var(--kp-text-2); }

  &.on {
    background: linear-gradient(180deg, rgba(216, 178, 95, 0.26), rgba(168, 124, 57, 0.10));
    color: var(--kp-gold-hi);
    font-weight: 700;
    box-shadow:
      0 0 14px -7px rgba(240, 208, 138, 0.65),
      inset 0 1px 0 rgba(255, 242, 208, 0.14);
  }
}

// ===== 实时状态条 =====
.status-strip {
  display: flex;
  // 手机上一行放完，不再折行；宽度不够时只压缩房间名
  flex-wrap: nowrap;
  gap: 8px 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--kp-gold-line);
  background: rgba(255, 240, 210, 0.015);
  overflow: hidden;
  flex-shrink: 0;

  .ss-item {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
    flex: 0 0 auto;
  }

  // 只有房间名可被压缩（省略号），状态/时间保持完整
  .ss-room {
    flex: 0 1 auto;

    .ss-val {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .ss-label {
    font-size: 10px;
    color: var(--kp-faint);
    letter-spacing: .4px;
    flex-shrink: 0;
  }

  .ss-val {
    font-size: 12px;
    font-weight: 600;
    color: var(--kp-text-2);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    &.on { color: var(--kp-jade); }
    &.off { color: var(--kp-muted); }
  }

  // 受控开关：开关本体与标签垂直居中（其它 ss-item 是基线对齐）
  .ss-switch {
    align-items: center;
    gap: 8px;
  }

  // 切换失败的行内提示（项目未挂 n-message-provider）
  .ss-err {
    font-size: 10px;
    color: var(--kp-bad-soft);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// ===== 授权设备条（页面底部一窄条：分页之上、不随列表滚动）=====
.device-strip {
  display: flex;
  // 只占一行：绝不换行（换行会顶出条外），宽度不够时由 IP 段省略号收尾
  flex-wrap: nowrap;
  align-items: center;
  gap: 0 8px;
  overflow: hidden;
  // 悬浮按钮已下移到分页那一行（见 .float-shot / .float-refresh 的 bottom），
  // 不再与本条竖向重叠，所以这里恢复常规内边距，把宽度全留给文字
  padding: 5px 16px;
  border-top: 1px solid var(--kp-gold-line);
  // 贴底一窄条：用接近卡片底的深色 + 上方一道极淡投影，做出“浮在列表之上”的层次
  background: linear-gradient(180deg, rgba(23, 19, 14, 0.92), rgba(14, 12, 10, 0.97));
  box-shadow: 0 -8px 14px -12px rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(8px);
  flex-shrink: 0;

  // 紧挨着的分页栏不再重复画分隔线（两者合成一条底栏）
  & + .pager-bar {
    border-top: 0;
  }

  .ds-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: var(--kp-faint);

    // 在线（有浏览器窗口）
    &.on {
      background: linear-gradient(180deg, #b6f0cf, #4cc98a);
      box-shadow: 0 0 8px rgba(76, 201, 138, 0.7);
    }

    // 扩展连接还在，但浏览器窗口已全部关闭
    &.closed {
      background: linear-gradient(180deg, #f5dfae, #d9a24f);
      box-shadow: 0 0 8px rgba(217, 162, 79, 0.55);
    }

    &.off { background: var(--kp-faint); }
  }

  // 状态文字：与授权管理页设备卡片同色（绿 / 金 / 灰）
  .ds-state {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .3px;
    white-space: nowrap;
    flex: 0 0 auto;

    &.on { color: #7ec9a4; }
    &.closed { color: #d9a24f; }
    &.off { color: var(--kp-faint); }
  }

  // 授权状态：已授权低调（正常态），已禁用用软红提示（授权管理里被关掉的设备）
  .ds-auth {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .3px;
    white-space: nowrap;
    flex: 0 0 auto;
    color: var(--kp-muted);

    &.is-off { color: #e08a75; }
  }

  .ds-item {
    display: flex;
    align-items: baseline;
    gap: 4px;
    min-width: 0;
    // 除 IP 段（可压缩省略）外，其余都按内容宽度固定
    flex: 0 0 auto;
  }

  .ds-label {
    font-size: 10px;
    color: var(--kp-faint);
    letter-spacing: .6px;
    white-space: nowrap;
    flex: 0 0 auto;
  }

  .ds-val {
    font-size: 12px;
    font-weight: 600;
    color: var(--kp-text-2);
    font-variant-numeric: tabular-nums;

    &.lat-good { color: var(--kp-jade); }
    &.lat-warn { color: var(--kp-gold-hi); }
    &.lat-bad { color: var(--kp-bad-soft); }
  }

  // 授权码是最需要一眼认出的字段
  .ds-code {
    color: var(--kp-gold-hi);
    font-weight: 800;
    letter-spacing: .4px;
  }

  // 插件版本：等宽小字；旧版插件（未上报版本）用更暗的常规字体区分
  .ds-ver {
    flex: 0 0 auto;

    .ds-val {
      font-family: var(--kp-mono);
      font-size: 11px;
      font-weight: 600;
      color: var(--kp-muted);
    }

    &.is-old .ds-val {
      font-family: inherit;
      font-size: 10px;
      letter-spacing: .3px;
      color: var(--kp-faint);
    }
  }

  // 归属地段可压缩：宽度不够时只在这里收省略号；点它弹出 IP 卡片
  .ds-region {
    flex: 1 1 auto;
    min-width: 0;
    cursor: pointer;

    .ds-val {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    // 点击提示：只靠悬停变色，不加多余标签占地方
    &:hover .ds-val {
      color: var(--kp-gold-hi);
    }
  }
}

// ===== IP 小卡片（点归属地弹出）=====
.ip-mask {
  position: fixed;
  inset: 0;
  z-index: 4000;
}

.ip-card {
  position: fixed;
  left: 50%;
  bottom: 92px;
  transform: translateX(-50%);
  z-index: 4001;
  min-width: 168px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-radius: 10px;
  background: linear-gradient(180deg, rgba(27, 22, 16, 0.98), rgba(12, 10, 8, 0.98));
  border: 1px solid rgba(216, 178, 95, 0.38);
  box-shadow:
    0 16px 40px -16px rgba(0, 0, 0, 0.92),
    0 0 0 1px rgba(216, 178, 95, 0.08);

  .ip-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
  }

  .ip-k {
    font-size: 10px;
    letter-spacing: .8px;
    color: var(--kp-faint);
    flex: 0 0 auto;
  }

  .ip-v {
    font-size: 12px;
    color: var(--kp-text-2);
    flex: 1 1 auto;
    text-align: right;
  }

  .ip-addr {
    font-family: var(--kp-mono);
    font-weight: 700;
    color: var(--kp-gold-hi);
    letter-spacing: .5px;
  }

  .ip-ver {
    font-family: var(--kp-mono);
    color: var(--kp-muted);
  }
}

// ===== 设备截图预览（Teleport 到 body 的全屏浮层）=====
.shot-mask {
  position: fixed;
  inset: 0;
  // 要盖住总控悬浮窗(2000)与锚点大面板(3000)
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: rgba(4, 6, 12, 0.82);
  backdrop-filter: blur(4px);
}

.shot-panel {
  display: flex;
  flex-direction: column;
  max-width: 100%;
  max-height: 100%;
  background: linear-gradient(180deg, rgba(23, 19, 14, 0.98), rgba(14, 12, 10, 0.98));
  border: 1px solid var(--kp-gold-line-strong);
  border-radius: 12px;
  box-shadow: 0 18px 48px -18px rgba(0, 0, 0, 0.9);
  overflow: hidden;
}

.shot-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--kp-gold-line);
  flex-shrink: 0;

  .shot-title {
    font-size: 12px;
    font-weight: 800;
    letter-spacing: .5px;
    color: var(--kp-gold-hi);
  }

  .shot-meta {
    font-size: 11px;
    color: var(--kp-muted);
    font-variant-numeric: tabular-nums;
  }

  .shot-link {
    margin-left: auto;
    font-size: 11px;
    color: var(--kp-gold);
    text-decoration: none;

    &:hover { color: var(--kp-gold-hi); text-decoration: underline; }
  }

  .shot-close {
    cursor: pointer;
    font-size: 12px;
    line-height: 1;
    padding: 3px 6px;
    border-radius: 6px;
    color: var(--kp-muted);
    transition: color .15s, background .15s;

    &:hover {
      color: var(--kp-gold-hi);
      background: rgba(216, 178, 95, 0.12);
    }
  }
}

.shot-body {
  padding: 8px;
  overflow: auto;
  min-height: 0;
}

.shot-img {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 140px);
  object-fit: contain;
  border-radius: 6px;
}

// ===== 统计条（紧凑单行：标签+数值内联）=====
.stats-bar {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 6px 16px;
  border-bottom: 1px solid var(--kp-gold-line);
  flex-shrink: 0;
}

.stat-card {
  display: flex;
  align-items: baseline;
  gap: 6px;

  &.card-win .sc-label { color: var(--kp-jade); }

  &.card-lose .sc-label { color: var(--kp-bad-soft); }
}

.sc-val {
  font-size: 15px;
  font-weight: 800;
  color: var(--kp-gold-hi);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  font-family: var(--kp-mono);

  &.positive { color: var(--kp-jade); }
  &.negative { color: var(--kp-bad-soft); }
}

.sc-label {
  font-size: 10px;
  color: var(--kp-muted);
  font-weight: 500;
  letter-spacing: .8px;
}

// 荷官信息：主播名用正文色（比金额小一号），重名时转琥珀色并挂一个红色「重名」角标
.stat-card.stat-dealer {
  .dealer-name {
    font-size: 13px;
    font-weight: 700;
    font-family: inherit;
    letter-spacing: .5px;
    color: var(--kp-text);

    &.dup { color: #e0b055; }
  }

  .dup-tag {
    align-self: center;
    height: 15px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    font-size: 9.5px;
    font-weight: 700;
    white-space: nowrap;
    color: #e08a75;
    background: rgba(224, 138, 117, 0.10);
    border: 1px solid rgba(224, 138, 117, 0.35);
    // 改成可点击按钮后就地切换策略：清掉 button 默认样式
    font-family: inherit;
    line-height: 1;
    cursor: pointer;
    transition: filter .15s, opacity .15s;
    -webkit-tap-highlight-color: transparent;

    &:hover { filter: brightness(1.18); }
    &:active { filter: brightness(.95); }
    &:disabled { cursor: wait; opacity: .65; }

    // 避让（默认）：房间不会参与托管，属"已处理"，用金色收敛，不报警
    &.is-avoid {
      color: #d8b25f;
      background: rgba(216, 178, 95, 0.12);
      border-color: rgba(216, 178, 95, 0.38);
    }

    // 智能清理模式：不做避让，用绿色与"避让（金）/照常（红）"区分
    &.is-clean {
      color: #6fd08c;
      background: rgba(111, 208, 140, 0.12);
      border-color: rgba(111, 208, 140, 0.42);
    }

    // 切换失败：短暂红一下，让用户知道没写进去
    &.failed {
      color: #ef4444;
      background: rgba(239, 68, 68, 0.12);
      border-color: rgba(239, 68, 68, 0.45);
    }
  }
}

// 房间 CD：与锚点小地图里的「CD Ns」同色（金色数字），一眼能对上
.stat-card.stat-cd {
  .cd-val {
    font-size: 13px;
    font-weight: 800;
    color: #fcd34d;
  }
}

// 窄屏：统计条（场次/收入/荷官/CD）允许换行，不横向溢出
@media (max-width: 640px) {
  .stats-bar {
    flex-wrap: wrap;
    gap: 4px 14px;
  }
}

// ===== 内容区（列表 + 分页）=====
.content-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 10px;
  min-height: 0;
  min-width: 0;
  // 宽屏限宽居中：给 3 列网格留足空间又不拉满全屏；手机（<900px）自动满宽
  width: 100%;
  max-width: 1560px;
  margin: 0 auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.20);
    border-radius: 2px;
  }
}

.loading, .empty {
  text-align: center;
  padding: 80px 0;
  color: var(--kp-faint);
  font-size: 13px;
  letter-spacing: 1px;
}

// ===== 记录网格：手机 1 列；≥900px 两列；≥1400px 三列（卡片内部始终紧凑单列）=====
.record-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  min-width: 0;
}

// ===== 记录卡片 =====
// 内部 flex 列：头部（含展开的托管原因）固定高度 → 中部（牌面/等待态）自动撑满 →
// 收益行沉底。同行某张卡展开变高时，兄弟卡片的中部被拉高填充，不会中间空一块。
.record {
  display: flex;
  flex-direction: column;
  background: linear-gradient(160deg, #211a12, #181208);
  border: 1px solid rgba(216, 178, 95, 0.16);
  border-radius: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 235, 198, 0.05);
  padding: 10px 12px;
  transition: all .2s;
  overflow: hidden;
  min-width: 0;

  &:hover {
    background: linear-gradient(160deg, #292114, #1d160c);
    border-color: rgba(216, 178, 95, 0.30);
  }

  &.unfinished {
    border-color: rgba(216, 178, 95, 0.30);
  }

  // 有托管结论的卡片可点开看原因（整条都是点击区域）
  &.clickable { cursor: pointer; }

  // 展开态：边框提亮，和未展开的卡片区分
  &.expanded {
    border-color: rgba(216, 178, 95, 0.55);
    box-shadow: inset 0 1px 0 rgba(255, 235, 198, 0.05), 0 0 0 1px rgba(216, 178, 95, 0.18);
  }

  // 玩家输赢着色（跟在 .unfinished 之后，边框色可覆盖它）：
  // 正在下注=琥珀金 / 赢=绿 / 输=红 / 和=中性金，深色底 + 同色系淡边，扫描一眼定位
  &.res-betting {
    background: linear-gradient(160deg, #2f2410, #221a08);
    border-color: rgba(245, 158, 11, 0.42);
    &:hover { background: linear-gradient(160deg, #3b2d13, #2a2009); }
  }

  &.res-win {
    background: linear-gradient(160deg, #14291e, #0e1c14);
    border-color: rgba(126, 201, 164, 0.40);
    &:hover { background: linear-gradient(160deg, #193424, #102418); }
  }

  &.res-lose {
    background: linear-gradient(160deg, #2e1815, #20100e);
    border-color: rgba(205, 106, 83, 0.40);
    &:hover { background: linear-gradient(160deg, #3b1f1a, #28130f); }
  }

  // 和（退本金）：中性金，比「正在下注」的琥珀更暗更淡，避免两者混淆
  &.res-tie {
    background: linear-gradient(160deg, #221d12, #181410);
    border-color: rgba(216, 178, 95, 0.32);
    &:hover { background: linear-gradient(160deg, #2b2517, #1e1913); }
  }
}

// ===== 卡片头部 =====
// 纵向布局：row1（标签行）与托管原因小行各占整行，原因太长时自然换行，不会横向挤压
// flex:none：头部高度随内容（展开原因时变高），不参与中部的撑满分配
.rec-top {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  flex: none;
}

.rec-top-row1 {
  display: flex;
  align-items: center;
  gap: 8px;
}

// 脉冲金点 - 直播中
.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(180deg, #fdf0cd, #d8b25f);
  flex-shrink: 0;
  box-shadow: 0 0 8px rgba(240, 208, 138, 0.7);
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.3); }
}

.rec-desk {
  font-size: 13px;
  color: var(--kp-text);
  font-weight: 700;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

// 房间编号（3001-57）：跟在房间号后面的弱化小字，不抢眼
.rec-room-no {
  font-style: normal;
  font-size: 9px;
  font-weight: 500;
  color: var(--kp-faint, #8a7d63);
  margin-left: 5px;
}

.rec-status {
  font-size: 10px;
  color: var(--kp-jade);
  background: rgba(126, 201, 164, 0.12);
  border: 1px solid rgba(126, 201, 164, 0.20);
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 600;

  &.done {
    color: var(--kp-muted);
    background: rgba(255, 240, 210, 0.05);
    border-color: rgba(216, 178, 95, 0.12);
  }
}

.rec-time {
  font-size: 10px;
  color: var(--kp-faint);
  white-space: nowrap;
}

.rec-round {
  font-size: 10px;
  color: var(--kp-gold);
  font-weight: 600;
  white-space: nowrap;
}

.rec-settle-time {
  font-size: 9px;
  color: var(--kp-faint);
  white-space: nowrap;
}

.rec-result-chip {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 11px;
  border-radius: 6px;
  margin-left: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  // 加实一点的背景 + 边框，让文字从深色卡片背景里浮出来
  background: rgba(255, 240, 210, 0.10);
  color: var(--kp-text-2);
  border: 1px solid rgba(216, 178, 95, 0.22);

  i {
    font-style: normal;
    color: var(--kp-gold);
    font-weight: 500;
    margin-left: 2px;
  }

  &.result-win {
    color: #bff0d6;
    background: rgba(126, 201, 164, 0.18);
    border-color: rgba(126, 201, 164, 0.40);
  }

  &.result-lose {
    color: #f3b6a6;
    background: rgba(205, 106, 83, 0.18);
    border-color: rgba(205, 106, 83, 0.40);
  }

  &.result-tie {
    color: #f2dcab;
    background: rgba(216, 178, 95, 0.18);
    border-color: rgba(216, 178, 95, 0.42);
  }

  // 已下注·未开奖：蓝色，区别于结算后的赢(绿)/输(红)/平(金)
  &.result-betting {
    color: #bfe0f7;
    background: rgba(96, 165, 250, 0.16);
    border-color: rgba(96, 165, 250, 0.42);
  }
}

// 托管结论小标签：托管(红)/自然(绿)/未托管(琥珀)，跟在「已结束」后面；可点开看原因
.rec-kill-tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  // ▾ 角标：提示「可点开」；展开时旋转 180°
  .k-arrow {
    font-style: normal;
    font-size: 8px;
    opacity: 0.75;
    transition: transform 0.15s;
  }
  &.open .k-arrow { transform: rotate(180deg); }

  &.kill {
    color: #ffb4a6;
    background: rgba(205, 106, 83, 0.20);
    border: 1px solid rgba(205, 106, 83, 0.45);
  }

  &.natural {
    color: #9fd8b4;
    background: rgba(126, 201, 164, 0.15);
    border: 1px solid rgba(126, 201, 164, 0.40);
  }

  // 未托管：托管按钮已开但本局跳过（条件不符/无锚点/无人下注等），悬停有具体原因
  &.noskill {
    color: #fcd34d;
    background: rgba(245, 158, 11, 0.16);
    border: 1px solid rgba(245, 158, 11, 0.45);
  }
}

// 点按托管标签展开的完整托管原因小行（手机端没有悬停，靠这个展示；桌面也可点）
.rec-kill-desc {
  margin-top: 5px;
  padding: 4px 7px;
  font-size: 10px;
  line-height: 1.5;
  color: var(--kp-faint, #a89880);
  background: rgba(255, 240, 210, 0.04);
  border-left: 2px solid rgba(216, 178, 95, 0.35);
  border-radius: 0 4px 4px 0;
  word-break: break-all;
}

// 触屏设备（手机/平板）：没有悬停，全靠点按 —— 放大托管标签的点击区域
@media (hover: none) and (pointer: coarse) {
  .rec-kill-tag {
    font-size: 11px;
    padding: 6px 9px;
    min-height: 26px;

    .k-arrow { font-size: 9px; }
  }
}

// ===== PK 对战区 =====
.pk-zone {
  position: relative;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.02);
  border: 1px solid rgba(216, 178, 95, 0.10);
  flex: 1 1 auto; // 与其它中部区块一致：撑满卡片中部
  min-height: 96px;
  box-sizing: border-box;
}

.pk-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  flex-wrap: wrap;
}

.pk-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 5px;
  flex-shrink: 0;
  min-width: 36px;
  text-align: center;

  &.blue {
    color: var(--kp-gold-hi);
    background: rgba(216, 178, 95, 0.12);
    border: 1px solid rgba(216, 178, 95, 0.24);
  }
  &.red {
    color: var(--kp-bad-soft);
    background: rgba(205, 106, 83, 0.12);
    border: 1px solid rgba(205, 106, 83, 0.24);
  }
}

.pk-cards {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  min-width: 0;
}

.pk-pts {
  margin-left: auto;
  font-size: 16px;
  font-weight: 900;
  color: var(--kp-gold-hi);
  letter-spacing: 1px;
  flex-shrink: 0;
  text-shadow: 0 0 14px rgba(216, 178, 95, 0.35);

  i {
    font-style: normal;
    font-size: 10px;
    color: var(--kp-faint);
    font-weight: 500;
    margin-left: 2px;
  }
}

.pk-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  margin: 3px 0 3px 36px;
  position: relative;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    height: 1px;
    width: calc(50% - 24px);
    background: rgba(216, 178, 95, 0.10);
  }
  &::before { left: 0; }
  &::after  { right: 0; }

  span {
    font-size: 10px;
    font-weight: 800;
    color: var(--kp-faint);
    letter-spacing: 1px;
    padding: 0 8px;
    z-index: 1;
  }
}


// ===== 礼物标记 =====
.chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  line-height: 1.2;
  letter-spacing: 0.3px;
  border: 1px solid transparent;

  &.spade   { color: #e2b87a; border-color: rgba(226,184,122,0.22); background: rgba(226,184,122,0.08); }
  &.club    { color: #a9e3c6; border-color: rgba(169,227,198,0.20); background: rgba(169,227,198,0.06); }
  &.heart   { color: #f0a094; border-color: rgba(240,160,148,0.22); background: rgba(240,160,148,0.08); }
  &.diamond { color: #a9c6cf; border-color: rgba(169,198,207,0.20); background: rgba(169,198,207,0.06); }

  &.empty {
    color: var(--kp-faint);
    font-weight: 400;
    background: transparent;
    border-color: transparent;
  }
}

// ===== 真实扑克牌对决布局 =====
// 与 .pk-live（无牌面占位）盒模型完全一致：同 padding/gap/最小高度，flex:1 撑满卡片中部。
// 同行某张卡展开变高时，这里被拉高填充，牌面保持垂直居中 —— 不会中间空一块。
.pk-real-showdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.025);
  border: 1px solid rgba(216, 178, 95, 0.12);
  flex: 1 1 auto;
  min-height: 96px;
  box-sizing: border-box;
}

.showdown-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: none;
}

// ===== 无牌面占位区（正在下注 / 无牌记录）=====
// 盒模型与 .pk-real-showdown 完全一致（同 padding/gap/min-height + flex:1），两者高度严格对齐
.pk-live {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.02);
  border: 1px dashed rgba(216, 178, 95, 0.18);
  flex: 1 1 auto;
  min-height: 96px;
  box-sizing: border-box;
}

.live-wait {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
}

.live-wait-txt {
  font-size: 12px;
  font-weight: 600;
  color: var(--kp-faint, #8a7d63);
  letter-spacing: 1px;
  white-space: nowrap;
}

// 背面朝上的牌：斜纹 + 金边，左右两张微微倾角，中间放提示文字
.card-back {
  width: 26px;
  height: 36px;
  border-radius: 4px;
  flex-shrink: 0;
  background:
    repeating-linear-gradient(45deg, rgba(216, 178, 95, 0.12) 0 3px, transparent 3px 6px),
    linear-gradient(145deg, #241c10, #17110a);
  border: 1px solid rgba(216, 178, 95, 0.30);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  transform: rotate(-5deg);

  &:nth-of-type(2) { transform: rotate(-2deg); }
  &:nth-of-type(3) { transform: rotate(2deg); }
  &:nth-of-type(4) { transform: rotate(5deg); }
}

.showdown-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1; // 撑满中部剩余高度，牌面垂直居中（与 .live-wait 一致）
  min-height: 42px;
}

// 对决中心
.showdown-vs {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 8px;
  flex-shrink: 0;

  .vs-score-xian {
    font-size: 22px;
    font-weight: 900;
    color: var(--kp-gold-hi);
    font-variant-numeric: tabular-nums;
    line-height: 1;
    text-shadow: 0 0 16px rgba(216, 178, 95, 0.40);
  }
  .vs-score-zhuang {
    font-size: 22px;
    font-weight: 900;
    color: var(--kp-bad-soft);
    font-variant-numeric: tabular-nums;
    line-height: 1;
    text-shadow: 0 0 16px rgba(205, 106, 83, 0.35);
  }
  .vs-word {
    font-size: 10px;
    font-weight: 700;
    color: rgba(216, 178, 95, 0.30);
    letter-spacing: 0.5px;
    text-transform: lowercase;
    flex-shrink: 0;
  }
}

// ===== 传统扑克牌 =====
.playing-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 42px;
  border-radius: 4px;
  background: linear-gradient(145deg, #f7f2e6, #e9e2d0);
  border: 1px solid rgba(216,178,95,0.45);
  box-shadow:
    0 1px 3px rgba(0,0,0,0.35),
    0 0 12px -6px rgba(216,178,95,0.55),
    inset 0 0 0 1px rgba(255,255,255,0.28);
  flex-shrink: 0;

  .pc-rank {
    font-size: 14px;
    font-weight: 900;
    line-height: 1;
  }

  .pc-suit {
    font-size: 14px;
    line-height: 1;
    margin-top: -1px;
  }

  &.spade, &.club {
    .pc-rank, .pc-suit { color: #1a1a2e; }
  }
  &.heart, &.diamond {
    .pc-rank, .pc-suit { color: #c53030; }
  }

  &.empty {
    background: rgba(255,240,210,0.02);
    border: 1px dashed rgba(216,178,95,0.18);
    box-shadow: none;
    .pc-rank { color: var(--kp-faint); font-weight: 400; }
  }
}

// ===== 收益明细 =====
// flex:none + margin-top:auto：永远沉在卡片最底部（中部区块撑满时自然到底，双保险）
.rec-amounts {
  display: flex;
  justify-content: space-around;
  min-width: 0;
  flex: none;
  margin-top: auto;
}

.am {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

// 仅 PC 宽屏展示的收益项（手机隐藏，保持原来的三项不变）
.am-pc-only { display: none; }

.am-icon {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: var(--kp-gold);
  background: rgba(216, 178, 95, 0.10);
  border: 1px solid rgba(216, 178, 95, 0.18);
  border-radius: 3px;
  flex-shrink: 0;
}

.am-val {
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-family: var(--kp-mono);

  &.gold    { color: var(--kp-gold-hi); text-shadow: 0 0 12px rgba(216, 178, 95, 0.30); }
  &.positive { color: var(--kp-jade); }
  &.negative { color: var(--kp-bad-soft); }
  &.zero    { color: var(--kp-muted); }
  &.dim     { color: var(--kp-muted); font-size: 10px; font-weight: 400; }
}

// ===== 底部分页：固定 =====
.pager-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 10px 16px;
  border-top: 1px solid var(--kp-gold-line);
  flex-shrink: 0;
}

.pg-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  font-size: 16px;
  color: var(--kp-gold);
  cursor: pointer;
  user-select: none;
  transition: all .15s;

  &:hover {
    background: rgba(216, 178, 95, 0.12);
    color: var(--kp-gold-hi);
  }

  &.off {
    opacity: 0.25;
    pointer-events: none;
  }
}

.pg-num {
  font-size: 12px;
  color: var(--kp-muted);
  font-variant-numeric: tabular-nums;
  letter-spacing: .5px;
  min-width: 60px;
  text-align: center;
}

// ===== 悬浮刷新（右下：与底部分页同一行，不压住上面的设备条）=====
.float-refresh {
  position: absolute;
  right: 16px;
  bottom: 4px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
  color: var(--kp-gold);
  background: linear-gradient(180deg, rgba(216, 178, 95, 0.22), rgba(168, 124, 57, 0.10));
  border: 1px solid rgba(216, 178, 95, 0.34);
  cursor: pointer;
  user-select: none;
  transition: all .15s;
  z-index: 10;
  backdrop-filter: blur(6px);
  box-shadow:
    0 6px 20px -10px #000,
    0 0 22px -12px rgba(240, 208, 138, 0.65);

  &:hover {
    color: #fff6e0;
    background: linear-gradient(180deg, rgba(216, 178, 95, 0.34), rgba(168, 124, 57, 0.16));
    border-color: rgba(246, 227, 182, 0.60);
    box-shadow:
      0 6px 24px -10px #000,
      0 0 28px -10px rgba(240, 208, 138, 0.85);
  }

  &:active {
    transform: scale(0.9) rotate(180deg);
  }
}

// ===== 悬浮拍照（左下，与右下刷新对称；与分页同一行）=====
.float-shot {
  position: absolute;
  left: 16px;
  bottom: 4px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .5px;
  color: var(--kp-gold);
  background: linear-gradient(180deg, rgba(216, 178, 95, 0.22), rgba(168, 124, 57, 0.10));
  border: 1px solid rgba(216, 178, 95, 0.34);
  cursor: pointer;
  user-select: none;
  transition: all .15s;
  z-index: 10;
  backdrop-filter: blur(6px);
  box-shadow:
    0 6px 20px -10px #000,
    0 0 22px -12px rgba(240, 208, 138, 0.65);

  &:hover:not(.off) {
    color: #fff6e0;
    background: linear-gradient(180deg, rgba(216, 178, 95, 0.34), rgba(168, 124, 57, 0.16));
    border-color: rgba(246, 227, 182, 0.60);
    box-shadow:
      0 6px 24px -10px #000,
      0 0 28px -10px rgba(240, 208, 138, 0.85);
  }

  &:active:not(.off) { transform: scale(0.92); }

  // 设备离线：置灰且不可点（点击事件里也做了同样的拦截）
  &.off {
    color: var(--kp-muted);
    border-color: rgba(154, 144, 128, 0.30);
    background: rgba(255, 240, 210, 0.04);
    cursor: not-allowed;
    opacity: .7;
  }

  &.busy {
    color: var(--kp-muted);
    cursor: progress;
  }
}

// ===== PC 宽屏（≥900px）：记录多列排布，卡片保持紧凑单列 =====
// 手机（<900px）完全不受影响：1 列、原布局、三项收益行
@media (min-width: 900px) {
  // 两列；容器再宽（≥1400px）升三列
  .record-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }

  .record { padding: 11px 13px; }

  // 收益行在窄卡里允许换行，避免四项挤爆
  .rec-amounts { flex-wrap: wrap; gap: 5px 12px; }

  // PC 才显示的元素（有效投注）：手机上隐藏，收益行保持原样
  .am-pc-only { display: flex; }

  // 牌面适度放大（窄卡里放不下 38px，取折中）
  .playing-card { width: 34px; height: 48px; }

  // 顶栏区间按钮（今/昨/2d/3d/7d）：放大到和手机触屏版相当，不再“对不上号”
  .top-actions { gap: 3px; padding: 4px; }
  .range-btn { font-size: 13px; padding: 7px 14px; border-radius: 7px; }

  // 卡片头部 chips 同步放大（状态 / 托管结论 / 下注角标）
  .rec-status { font-size: 12px; padding: 3px 10px; }
  .rec-kill-tag { font-size: 12px; padding: 5px 10px; }
  .rec-result-chip { font-size: 12px; padding: 4px 10px; }
}

@media (min-width: 1400px) {
  .record-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
</style>
